import mapValues from 'lodash/mapValues';
import { navigate, pushRoute } from '../lib/router.js';
import { createSelector } from 'reselect';
import {
  removeAuthHeaders,
  setAuthHeaders,
  setCustomNaming,
} from '../lib/session.js';
import { clearOktaConfig, hasOktaConfig } from '../lib/oktaConfig.js';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';
import { clearNewRelicUser, setNewRelicUser } from '../lib/newRelicBrowser.js';

// API Actions
const SIGN_IN_REQUEST = 'changesync/auth/SIGN_IN_REQUEST';
const SIGN_IN_SUCCESS = 'changesync/auth/SIGN_IN_SUCCESS';
const SIGN_IN_FAILURE = 'changesync/auth/SIGN_IN_FAILURE';
const SESSION_EXPIRED = 'changesync/auth/SESSION_EXPIRED';
const SESSION_RESTORED = 'changesync/auth/SESSION_RESTORED';
const FIND_ORGANIZATION_REQUEST = 'changesync/auth/FIND_ORGANIZATION_REQUEST';
const FIND_ORGANIZATION_SUCCESS = 'changesync/auth/FIND_ORGANIZATION_SUCCESS';
const FIND_ORGANIZATION_FAILURE = 'changesync/auth/FIND_ORGANIZATION_FAILURE';
const GET_USER_SSO_CONFIG_REQUEST =
  'changesync/auth/GET_USER_SSO_CONFIG_REQUEST';
const GET_USER_SSO_CONFIG_SUCCESS =
  'changesync/auth/GET_USER_SSO_CONFIG_SUCCESS';
const GET_USER_SSO_CONFIG_FAILURE =
  'changesync/auth/GET_USER_SSO_CONFIG_FAILURE';
const SIGN_OUT_REQUEST = 'changesync/auth/SIGN_OUT_REQUEST';
const SIGN_OUT_SUCCESS = 'changesync/auth/SIGN_OUT_SUCCESS';
const SIGN_OUT_FAILURE = 'changesync/auth/SIGN_OUT_FAILURE';
const RETRIEVE_USER_REQUEST = 'changesync/auth/RETRIEVE_USER_REQUEST';
const RETRIEVE_USER_SUCCESS = 'changesync/auth/RETRIEVE_USER_SUCCESS';
const RETRIEVE_USER_FAILURE = 'changesync/auth/RETRIEVE_USER_FAILURE';
const INVITE_STAKEHOLDER_REQUEST = 'changesync/auth/INVITE_STAKEHOLDER_REQUEST';
const INVITE_STAKEHOLDER_SUCCESS = 'changesync/auth/INVITE_STAKEHOLDER_SUCCESS';
const INVITE_STAKEHOLDER_FAILURE = 'changesync/auth/INVITE_STAKEHOLDER_FAILURE';
const ACCEPT_INVITATION_REQUEST = 'changesync/auth/ACCEPT_INVITATION_REQUEST';
const ACCEPT_INVITATION_SUCCESS = 'changesync/auth/ACCEPT_INVITATION_SUCCESS';
const ACCEPT_INVITATION_FAILURE = 'changesync/auth/ACCEPT_INVITATION_FAILURE';
const RESET_PASSWORD_REQUEST = 'changesync/auth/RESET_PASSWORD_REQUEST';
const RESET_PASSWORD_SUCCESS = 'changesync/auth/RESET_PASSWORD_SUCCESS';
const RESET_PASSWORD_FAILURE = 'changesync/auth/RESET_PASSWORD_FAILURE';
const SET_PASSWORD_REQUEST = 'changesync/auth/SET_PASSWORD_REQUEST';
const SET_PASSWORD_SUCCESS = 'changesync/auth/SET_PASSWORD_SUCCESS';
const SET_PASSWORD_FAILURE = 'changesync/auth/SET_PASSWORD_FAILURE';

export {
  SIGN_OUT_SUCCESS,
  RETRIEVE_USER_SUCCESS,
  SESSION_EXPIRED,
  SESSION_RESTORED,
  SIGN_IN_SUCCESS,
};

// Initial State
const initialState = {
  user: {},
  changePassword: {
    showModal: false,
  },
  sessionExpired: false,
};

// Reducer
export default (state = initialState, action) => {
  switch (action.type) {
    case SESSION_EXPIRED:
      return {
        ...state,
        sessionExpired: true,
      };
    case SESSION_RESTORED:
      return { ...state, sessionExpired: false };
    case SIGN_IN_SUCCESS:
    case RETRIEVE_USER_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        sessionExpired: false,
      };
    case ACCEPT_INVITATION_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
      };
    case SIGN_OUT_SUCCESS:
      return {
        ...state,
        user: {},
      };

    default:
      return state;
  }
};

// Selectors

export const selectUser = (state) => state.auth.user;

export const selectIsSignedIn = createSelector(
  selectUser,
  (user) => typeof user.id === 'number'
);

export const selectCurrentOrganizationId = createSelector(
  selectUser,
  (user) => user.organizationId || user.organization_id
);

export const selectUserPermissions = createSelector(selectUser, (user) =>
  mapValues(user.permissions?.can, Object.keys)
);

export const selectUserRestrictions = createSelector(selectUser, (user) =>
  mapValues(user.permissions?.cannot, Object.keys)
);

export const selectSessionExpired = (state) => state.auth.sessionExpired;

// Action Creators

export const retrieveUser = (query) => ({
  [CALL_API]: {
    types: [
      RETRIEVE_USER_REQUEST,
      RETRIEVE_USER_SUCCESS,
      RETRIEVE_USER_FAILURE,
    ],
    endpoint: 'users/me',
    query,
  },
});

export const signIn = (user, organizationSlug) => (dispatch) => {
  // Clear old session data before signing in
  removeAuthHeaders();

  // Clear old Okta config if exists (in case switching from SSO to regular login)
  if (hasOktaConfig()) {
    clearOktaConfig();
  }

  const signInAction = {
    [CALL_API]: {
      types: [SIGN_IN_REQUEST, SIGN_IN_SUCCESS, SIGN_IN_FAILURE],
      endpoint: 'users/sign_in',
      method: HTTP_METHODS.POST,
      body: { user },
      withCredentials: true,
      skipAuthRedirect: true,
    },
  };

  return dispatch(signInAction)
    .then((response) => {
      setAuthHeaders(response.authToken);
      setCustomNaming(response.user);
      setNewRelicUser({
        id: response.user.id,
        email: response.user.email,
        organizationId: response.user.organizations[0]?.id,
        organizationName: response.user.organizations[0]?.name,
        role: response.user.role,
      });
      return response;
    })
    .then((response) => {
      dispatch(
        retrieveUser({
          organization_slug:
            organizationSlug || response.user.organizations[0].slug,
        })
      );
      return response;
    })
    .then((response) => {
      return pushRoute('projects/index', {
        slug: organizationSlug || response.user.organizations[0].slug,
      });
    });
};

// Sign in without navigating away — used by the session-expired modal so the
// user is returned to whatever page they were on after re-authenticating.
export const signInAndRestore = (user, organizationSlug) => (dispatch) => {
  const signInAction = {
    [CALL_API]: {
      types: [SIGN_IN_REQUEST, SIGN_IN_SUCCESS, SIGN_IN_FAILURE],
      endpoint: 'users/sign_in',
      method: HTTP_METHODS.POST,
      body: { user },
      withCredentials: true,
      skipAuthRedirect: true,
    },
  };

  return dispatch(signInAction)
    .then((response) => {
      // removeAuthHeaders();

      // if (hasOktaConfig()) {
      //   clearOktaConfig();
      // }
      setAuthHeaders(response.authToken);
      setCustomNaming(response.user);
      setNewRelicUser({
        id: response.user.id,
        email: response.user.email,
        organizationId: response.user.organizations[0]?.id,
        organizationName: response.user.organizations[0]?.name,
        role: response.user.role,
      });
      return response;
    })
    .then((response) => {
      dispatch(
        retrieveUser({
          organization_slug:
            organizationSlug || response.user.organizations[0].slug,
        })
      );
      return response;
    });
  // Intentionally no navigation — the modal closes via SIGN_IN_SUCCESS reducer
  // and the user stays on the current page.
};

export const signInSso = (accessToken, idToken) => (dispatch) => {
  // Clear old session data before signing in
  removeAuthHeaders();

  const signInAction = {
    [CALL_API]: {
      types: [SIGN_IN_REQUEST, SIGN_IN_SUCCESS, SIGN_IN_FAILURE],
      endpoint: 'sessions/okta',
      method: HTTP_METHODS.POST,
      customHeader: {
        Authorization: `Bearer ${idToken}`,
        AccessToken: `Bearer ${accessToken}`,
      },
      withCredentials: true,
    },
  };

  return dispatch(signInAction)
    .then((response) => {
      setAuthHeaders(response.authToken);
      setCustomNaming(response.user);
      setNewRelicUser({
        id: response.user.id,
        email: response.user.email,
        organizationId: response.user.organizations[0]?.id,
        organizationName: response.user.organizations[0]?.name,
        role: response.user.role,
      });
      return response;
    })
    .then((response) => {
      dispatch(
        retrieveUser({ organization_slug: response.user.organizations[0].slug })
      );
      return response;
    })
    .then((response) => {
      return pushRoute('projects/index', {
        slug: response.user.organizations[0].slug,
      });
    });
};

export const findUserOrganizations = (email) => ({
  [CALL_API]: {
    types: [
      FIND_ORGANIZATION_REQUEST,
      FIND_ORGANIZATION_SUCCESS,
      FIND_ORGANIZATION_FAILURE,
    ],
    endpoint: 'organizations',
    method: HTTP_METHODS.GET,
    query: { email },
  },
});

export const getUserSsoConfig = (organization) => ({
  [CALL_API]: {
    types: [
      GET_USER_SSO_CONFIG_REQUEST,
      GET_USER_SSO_CONFIG_SUCCESS,
      GET_USER_SSO_CONFIG_FAILURE,
    ],
    endpoint: 'organizations/sso_login',
    method: HTTP_METHODS.GET,
    query: { organization },
  },
});

export const acceptInvitation = (user, organizationSlug) => (dispatch) => {
  const acceptInvitationAction = {
    [CALL_API]: {
      types: [
        ACCEPT_INVITATION_REQUEST,
        ACCEPT_INVITATION_SUCCESS,
        ACCEPT_INVITATION_FAILURE,
      ],
      endpoint: 'users/invitation',
      method: HTTP_METHODS.PUT,
      body: { user },
      withCredentials: true,
    },
  };

  return dispatch(acceptInvitationAction).then(() =>
    navigate(organizationSlug ? `/?organization_slug=${organizationSlug}` : '/')
  );
};

export const inviteStakeHolder = (user) => ({
  [CALL_API]: {
    types: [
      INVITE_STAKEHOLDER_REQUEST,
      INVITE_STAKEHOLDER_SUCCESS,
      INVITE_STAKEHOLDER_FAILURE,
    ],
    endpoint: 'users/invite_stakeholder',
    method: HTTP_METHODS.POST,
    body: { user },
  },
});

export const signOut = () => (dispatch) => {
  const signOutAction = {
    [CALL_API]: {
      types: [SIGN_OUT_REQUEST, SIGN_OUT_SUCCESS, SIGN_OUT_FAILURE],
      endpoint: 'users/sign_out',
      method: HTTP_METHODS.DELETE,
      withCredentials: true,
      skipAuthRedirect: true,
    },
  };

  return dispatch(signOutAction).then(async () => {
    removeAuthHeaders();
    clearNewRelicUser();

    if (hasOktaConfig()) {
      clearOktaConfig();
    }

    // Backend should clear refresh_token httpOnly cookie
    return navigate('/');
  });
};

export const resetPassword = (user) => ({
  [CALL_API]: {
    types: [
      RESET_PASSWORD_REQUEST,
      RESET_PASSWORD_SUCCESS,
      RESET_PASSWORD_FAILURE,
    ],
    endpoint: 'users/password',
    method: HTTP_METHODS.POST,
    body: { user },
  },
});

export const setPassword = (user) => ({
  [CALL_API]: {
    types: [SET_PASSWORD_REQUEST, SET_PASSWORD_SUCCESS, SET_PASSWORD_FAILURE],
    endpoint: 'users/password',
    method: HTTP_METHODS.PUT,
    body: { user },
  },
});
